import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, isFirebaseConfigured } from "../lib/firebase";
import { ManifestShell } from "../components/manifest/CosmicSky";
import CosmicReceipt from "../components/manifest/CosmicReceipt";
import { playAddBlip, playReleaseChime } from "../lib/manifestSounds";

// Universe & Co. — a little general store for placing manifestation orders,
// in the spirit of the Law of Assumption: browse the aisles, say who it's
// for, and check out. Everything costs $0.00 (paid in trust); orders are
// saved quietly to Firestore and the confirmation is a paper receipt from
// the universe.

const CART_KEY = "manifestCart"; // localStorage: survives a refresh

// The catalog, arranged into aisles. Each item is a manifestation you can
// "order". Item input modes:
//   (none)          → a plain "Add ✦" card
//   input: "text"   → a free-write box; the words become the order note
//   input: "select" → a dropdown of options; `lineLabel(option)` builds the
//                     order's label (e.g. "See a red car")
// `price` is the whimsical cost shown on the shelf tag; `accent` picks the
// card's glow color from ACCENTS below.
const AISLES = [
  {
    id: "big",
    name: "Aisle One — The Big Stuff",
    tagline: "Staples of a good life. Stocked daily.",
    items: [
      {
        id: "abundance",
        emoji: "💰",
        label: "Money & Abundance",
        blurb: "Wealth flows to you easily and often.",
        price: "3 deep breaths",
        accent: "amber",
      },
      {
        id: "nourishment",
        emoji: "🍽️",
        label: "Nourishment & Free Food",
        blurb: "You are always fed, always provided for.",
        price: "1 grateful bite",
        accent: "rose",
      },
      {
        id: "health",
        emoji: "💪",
        label: "Health & Vitality",
        blurb: "Your body is strong, well, and full of life.",
        price: "a good night's sleep",
        accent: "emerald",
      },
      {
        id: "love",
        emoji: "❤️",
        label: "Love & Connection",
        blurb: "You are deeply loved and surrounded by warmth.",
        price: "1 open heart",
        accent: "rose",
      },
      {
        id: "peace",
        emoji: "🧘",
        label: "Peace of Mind",
        blurb: "Your mind is calm, clear, and unshakeable.",
        price: "5 quiet minutes",
        accent: "sky",
      },
      {
        id: "opportunity",
        emoji: "🌟",
        label: "Opportunity & Open Doors",
        blurb: "The right doors open at exactly the right time.",
        price: "1 brave yes",
        accent: "amber",
      },
      {
        id: "protection",
        emoji: "🛡️",
        label: "Protection & Safety",
        blurb: "You and yours are safe, guarded, and at ease.",
        price: "1 long exhale",
        accent: "violet",
      },
      {
        id: "energy",
        emoji: "⚡",
        label: "Energy & Drive",
        blurb: "You move through your days with momentum and spark.",
        price: "1 morning stretch",
        accent: "amber",
      },
      {
        id: "clarity",
        emoji: "🧭",
        label: "Clarity & Direction",
        blurb: "The fog lifts, and the next step is obvious.",
        price: "1 honest question",
        accent: "sky",
      },
      {
        id: "creativity",
        emoji: "🎨",
        label: "Creativity & Flow",
        blurb: "Ideas arrive faster than you can catch them.",
        price: "1 blank page, faced",
        accent: "violet",
      },
      {
        id: "confidence",
        emoji: "🦁",
        label: "Confidence",
        blurb: "You walk in like you belong — because you do.",
        price: "1 straightened spine",
        accent: "amber",
      },
      {
        id: "goodnews",
        emoji: "📬",
        label: "Good News",
        blurb: "Something wonderful is already in the mail.",
        price: "1 hopeful inbox check",
        accent: "emerald",
      },
    ],
  },
  {
    id: "winks",
    name: "Aisle Two — Little Winks",
    tagline: "Small signs, big smiles. Impulse shelf.",
    items: [
      {
        id: "car",
        emoji: "🚗",
        label: "See a car of a color",
        blurb: "A car in just the color you pick rolls past — your wink from the universe.",
        price: "1 glance out the window",
        accent: "sky",
        input: "select",
        optionPrompt: "Pick a color",
        options: [
          { value: "red", name: "red", emoji: "🔴" },
          { value: "orange", name: "orange", emoji: "🟠" },
          { value: "yellow", name: "yellow", emoji: "🟡" },
          { value: "green", name: "green", emoji: "🟢" },
          { value: "blue", name: "blue", emoji: "🔵" },
          { value: "purple", name: "purple", emoji: "🟣" },
          { value: "black", name: "black", emoji: "⚫" },
          { value: "white", name: "white", emoji: "⚪" },
          { value: "brown", name: "brown", emoji: "🟤" },
        ],
        lineLabel: (o) => `See a ${o.name} car`,
      },
      {
        id: "angel",
        emoji: "🔢",
        label: "Spot angel numbers",
        blurb: "Catch a meaningful sequence on a clock, receipt, or plate.",
        price: "1 well-timed glance",
        accent: "violet",
        input: "select",
        optionPrompt: "Pick a sequence",
        options: [
          { value: "111", name: "1:11" },
          { value: "222", name: "2:22" },
          { value: "333", name: "3:33" },
          { value: "444", name: "4:44" },
          { value: "555", name: "5:55" },
          { value: "1212", name: "12:12" },
          { value: "1234", name: "12:34" },
        ],
        lineLabel: (o) => `Spot ${o.name}`,
      },
      {
        id: "parking",
        emoji: "🅿️",
        label: "Perfect parking spot",
        blurb: "A front-row space opens up right as you pull in.",
        price: "1 hopeful lap of the lot",
        accent: "sky",
      },
      {
        id: "foundmoney",
        emoji: "🍀",
        label: "Find money on the ground",
        blurb: "A lucky bill or coin appears right at your feet.",
        price: "1 look down",
        accent: "emerald",
      },
      {
        id: "song",
        emoji: "🎵",
        label: "Your song comes on",
        blurb: "A song you love plays at the exact moment you need it.",
        price: "1 volume knob at the ready",
        accent: "violet",
      },
      {
        id: "treat",
        emoji: "☕",
        label: "A free treat",
        blurb: "Someone surprises you with a coffee, snack, or little gift.",
        price: "1 gracious thank-you",
        accent: "amber",
      },
      {
        id: "dog",
        emoji: "🐶",
        label: "A friendly dog says hi",
        blurb: "A happy pup trots over just to brighten your day.",
        price: "1 outstretched hand",
        accent: "rose",
      },
      {
        id: "greenlights",
        emoji: "🟢",
        label: "Green lights all the way",
        blurb: "Every light turns green as you roll up to it.",
        price: "1 steady cruise",
        accent: "emerald",
      },
      {
        id: "compliment",
        emoji: "💌",
        label: "An unexpected compliment",
        blurb: "Kind words land in your lap out of nowhere.",
        price: "1 shy smile",
        accent: "rose",
      },
      {
        id: "butterfly",
        emoji: "🦋",
        label: "A butterfly crosses your path",
        blurb: "A little flutter of magic drifts right past you.",
        price: "1 moment of looking up",
        accent: "sky",
      },
      {
        id: "rainbow",
        emoji: "🌈",
        label: "A rainbow appears",
        blurb: "Right when the rain lets up, there it is.",
        price: "1 glance after the rain",
        accent: "sky",
      },
      {
        id: "oldfriend",
        emoji: "📞",
        label: "An old friend reaches out",
        blurb: "A name you've missed lights up your phone.",
        price: "1 fond memory",
        accent: "rose",
      },
      {
        id: "firsttry",
        emoji: "🎯",
        label: "It works on the first try",
        blurb: "The code runs. The cake rises. The key turns.",
        price: "1 confident attempt",
        accent: "emerald",
      },
      {
        id: "weather",
        emoji: "☀️",
        label: "Perfect weather for your plans",
        blurb: "The sky checks your calendar and delivers.",
        price: "1 look at the sky",
        accent: "amber",
        input: "select",
        optionPrompt: "Pick your forecast",
        options: [
          { value: "sun", name: "☀️ sunshine", line: "A day of sunshine" },
          { value: "rain", name: "🌦️ gentle rain", line: "A gentle rain" },
          { value: "snow", name: "❄️ a snow day", line: "A snow day" },
          { value: "breeze", name: "🍃 a cool breeze", line: "A cool breeze" },
          {
            value: "storm",
            name: "⛈️ a thunderstorm to sleep to",
            line: "A thunderstorm to sleep to",
          },
        ],
        lineLabel: (o) => o.line,
      },
    ],
  },
  {
    id: "night",
    name: "Aisle Three — The Night Shelf",
    tagline: "Restocked at moonrise. For everything after dark.",
    items: [
      {
        id: "sleep",
        emoji: "😴",
        label: "Deep, restful sleep",
        blurb: "You sink in, and morning comes gently.",
        price: "1 phone, face-down",
        accent: "violet",
      },
      {
        id: "dream",
        emoji: "💭",
        label: "A dream worth retelling",
        blurb: "The kind you narrate at breakfast.",
        price: "1 fluffed pillow",
        accent: "sky",
      },
      {
        id: "earlybird",
        emoji: "⏰",
        label: "Wake before the alarm",
        blurb: "Eyes open, rested, with minutes to spare.",
        price: "1 early night",
        accent: "amber",
      },
      {
        id: "quietmorning",
        emoji: "🍵",
        label: "A slow, quiet morning",
        blurb: "No rush, a warm mug, soft light through the window.",
        price: "1 unhurried sip",
        accent: "emerald",
      },
    ],
  },
  {
    id: "counter",
    name: "The Counter — Special Orders",
    tagline: "Can't find it on a shelf? We'll make it to order.",
    items: [
      {
        id: "custom",
        emoji: "✍️",
        label: "Write your own",
        blurb: "Name exactly what you're calling in.",
        price: "whatever it takes",
        accent: "amber",
        input: "text",
      },
    ],
  },
];

// Tailwind needs literal class strings, so each accent is spelled out.
const ACCENTS = {
  amber: {
    card: "hover:border-amber-300/60 hover:shadow-[0_10px_50px_-12px_rgba(251,191,36,0.4)]",
    orb: "bg-amber-300/10 ring-amber-300/30",
    btn: "border-amber-300/50 bg-amber-300/10 text-amber-100 hover:bg-amber-300/25",
    focus: "focus:border-amber-300/60",
  },
  violet: {
    card: "hover:border-violet-300/60 hover:shadow-[0_10px_50px_-12px_rgba(167,139,250,0.4)]",
    orb: "bg-violet-400/10 ring-violet-300/30",
    btn: "border-violet-300/50 bg-violet-400/10 text-violet-100 hover:bg-violet-400/25",
    focus: "focus:border-violet-300/60",
  },
  rose: {
    card: "hover:border-rose-300/60 hover:shadow-[0_10px_50px_-12px_rgba(251,113,133,0.4)]",
    orb: "bg-rose-400/10 ring-rose-300/30",
    btn: "border-rose-300/50 bg-rose-400/10 text-rose-100 hover:bg-rose-400/25",
    focus: "focus:border-rose-300/60",
  },
  sky: {
    card: "hover:border-sky-300/60 hover:shadow-[0_10px_50px_-12px_rgba(125,211,252,0.4)]",
    orb: "bg-sky-400/10 ring-sky-300/30",
    btn: "border-sky-300/50 bg-sky-400/10 text-sky-100 hover:bg-sky-400/25",
    focus: "focus:border-sky-300/60",
  },
  emerald: {
    card: "hover:border-emerald-300/60 hover:shadow-[0_10px_50px_-12px_rgba(110,231,183,0.4)]",
    orb: "bg-emerald-400/10 ring-emerald-300/30",
    btn: "border-emerald-300/50 bg-emerald-400/10 text-emerald-100 hover:bg-emerald-400/25",
    focus: "focus:border-emerald-300/60",
  },
};

const RECIPIENT_PRESETS = ["Me", "Mom", "Dad", "Parents"];

const PLACING_MESSAGES = [
  "Wrapping your wishes…",
  "Addressing them to the stars…",
  "Handing the parcel to the universe…",
];

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const newOrderNo = () =>
  `UNI-${Date.now().toString(36).slice(-5).toUpperCase()}`;

// One shelf card in the catalog.
const WishCard = ({ item, value, onChange, onAdd, justAdded }) => {
  const a = ACCENTS[item.accent] || ACCENTS.amber;
  const addLabel = justAdded ? "✦ In the basket" : "Add ✦";

  return (
    <div
      className={`group flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 ${a.card}`}
    >
      <div className="mb-3 flex items-start justify-between">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-full text-2xl ring-1 transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-110 ${a.orb}`}
        >
          {item.emoji}
        </div>
        <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] uppercase tracking-wider text-slate-400">
          in stock: ∞
        </span>
      </div>

      <h3 className="font-serif text-lg text-white">{item.label}</h3>
      <p className="mt-1 flex-1 text-sm text-slate-300/80">{item.blurb}</p>

      {item.input === "text" && (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="I am so grateful that…"
          rows={3}
          className={`mt-3 w-full resize-none rounded-lg border border-white/15 bg-[#05050e]/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none ${a.focus}`}
        />
      )}

      {item.input === "select" && (
        <>
          <label className="mt-3 block text-xs uppercase tracking-wider text-slate-400">
            {item.optionPrompt}
          </label>
          <select
            value={value ?? item.options[0].value}
            onChange={(e) => onChange(e.target.value)}
            className={`mt-1 w-full rounded-lg border border-white/15 bg-[#0b0a22] px-3 py-2 text-sm text-slate-100 focus:outline-none ${a.focus}`}
          >
            {item.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.emoji ? `${o.emoji} ${o.name}` : o.name}
              </option>
            ))}
          </select>
        </>
      )}

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onAdd}
          disabled={item.input === "text" && !(value || "").trim()}
          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${a.btn}`}
        >
          {addLabel}
        </button>
        <span className="text-right text-xs italic text-slate-400">
          {item.price}
        </span>
      </div>
    </div>
  );
};

// Fixed basket dock at the bottom of the screen: shows the running order and
// holds the "Release" (checkout) button. Expands into a drawer to review or
// remove wishes.
const BasketDock = ({ cart, open, onToggle, onRemove, onRelease, placing, bump }) => {
  const summary =
    cart.length === 0
      ? "Your basket is empty — add a wish"
      : `${cart.length} ${cart.length === 1 ? "wish" : "wishes"} ready to release`;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4 sm:pb-6">
      <div className="mx-auto max-w-xl">
        {open && cart.length > 0 && (
          <div className="ms-pop mb-2 max-h-[45vh] overflow-y-auto rounded-2xl border border-white/15 bg-[#0b0a22]/95 p-4 shadow-[0_-10px_50px_-12px_rgba(0,0,0,0.8)] backdrop-blur-xl">
            <ul className="space-y-2">
              {cart.map((line) => (
                <li
                  key={line.key}
                  className="flex items-start justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm text-white">
                      <span>{line.emoji}</span>
                      <span className="truncate font-medium">{line.label}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-400">
                      for {line.recipient}
                      {line.note && <span className="italic"> · “{line.note}”</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(line.key)}
                    className="shrink-0 text-xs text-slate-500 transition-colors hover:text-rose-300"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center gap-3 rounded-full border border-white/15 bg-[#0b0a22]/95 py-2 pl-4 pr-2 shadow-[0_10px_50px_-10px_rgba(124,58,237,0.5)] backdrop-blur-xl">
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
            aria-expanded={open}
          >
            <span className="text-xl" aria-hidden>🧺</span>
            <span
              key={bump}
              className="ms-pop flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-amber-300/25 px-1.5 text-xs font-semibold text-amber-100"
            >
              {cart.length}
            </span>
            <span className="truncate text-sm text-slate-300">{summary}</span>
            {cart.length > 0 && (
              <span
                className={`ml-auto mr-1 text-xs text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
                aria-hidden
              >
                ▲
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onRelease}
            disabled={cart.length === 0 || placing}
            className="shrink-0 rounded-full border border-amber-300/70 bg-gradient-to-r from-amber-400/40 via-amber-300/25 to-violet-400/40 px-5 py-2 text-sm font-semibold text-amber-50 shadow-[0_0_25px_-6px_rgba(251,191,36,0.7)] transition-all hover:shadow-[0_0_35px_-4px_rgba(251,191,36,0.9)] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
          >
            Release ✨
          </button>
        </div>
      </div>
    </div>
  );
};

// Full-screen moment while the order is "sent": wishes rise into the sky.
const ReleaseOverlay = ({ message }) => {
  const sparks = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => ({
        left: 6 + ((i * 61) % 89),
        delay: (i % 8) * 0.3,
        dur: 2.4 + (i % 5) * 0.5,
        char: ["✦", "✧", "⋆", "·"][i % 4],
        size: 14 + (i % 4) * 6,
      })),
    []
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#05050e]/90 backdrop-blur-md">
      {sparks.map((s, i) => (
        <span
          key={i}
          aria-hidden
          className="ms-rise text-amber-200/90"
          style={{
            left: `${s.left}%`,
            fontSize: `${s.size}px`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur}s`,
          }}
        >
          {s.char}
        </span>
      ))}
      <div className="ms-float text-5xl" aria-hidden>📦</div>
      <p className="mt-6 font-serif text-xl text-amber-100">{message}</p>
    </div>
  );
};

const ConfirmationScreen = ({ order, onReset }) => (
  <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center py-10 text-center">
    <h1 className="ms-pop font-serif text-4xl font-semibold text-white sm:text-5xl">
      Ordered. <span className="ms-shimmer">It is done.</span>
    </h1>
    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-300">
      Here's your receipt. There's nothing left to do — release it, trust it's
      handled, and live as if it's already yours.
    </p>

    <div className="mt-10 w-full">
      <CosmicReceipt
        lines={order.lines}
        dateLabel={order.dateLabel}
        orderNo={order.orderNo}
      />
    </div>

    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
      <button
        type="button"
        onClick={onReset}
        className="rounded-full border border-amber-300/70 bg-amber-300/15 px-6 py-2.5 text-sm font-medium text-amber-100 transition-colors hover:bg-amber-300/25"
      >
        Place another order
      </button>
      <Link
        to="/manifest/log"
        className="rounded-full border border-white/15 bg-white/[0.04] px-6 py-2.5 text-sm text-slate-300 transition-colors hover:border-white/30"
      >
        Order history
      </Link>
    </div>
  </div>
);

const Manifest = () => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [recipient, setRecipient] = useState("Me");
  const [customRecipient, setCustomRecipient] = useState("");
  // Per-item input values, keyed by item id (free-write text or dropdown choice).
  const [inputs, setInputs] = useState({});
  const [placing, setPlacing] = useState(false);
  const [placingMsg, setPlacingMsg] = useState(PLACING_MESSAGES[0]);
  const [confirmed, setConfirmed] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const [bump, setBump] = useState(0);
  const addedTimer = useRef(null);

  const setInput = (id, value) =>
    setInputs((prev) => ({ ...prev, [id]: value }));

  // Persist the cart so a refresh mid-browse doesn't lose it.
  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [cart]);

  // Step the overlay copy along while an order is being released.
  useEffect(() => {
    if (!placing) return undefined;
    setPlacingMsg(PLACING_MESSAGES[0]);
    let i = 0;
    const t = setInterval(() => {
      i = Math.min(i + 1, PLACING_MESSAGES.length - 1);
      setPlacingMsg(PLACING_MESSAGES[i]);
    }, 900);
    return () => clearInterval(t);
  }, [placing]);

  useEffect(() => () => clearTimeout(addedTimer.current), []);

  // The recipient that will be attached to anything added right now.
  const activeRecipient = useMemo(() => {
    const custom = customRecipient.trim();
    return custom || recipient;
  }, [customRecipient, recipient]);

  const addItem = (item) => {
    let label = item.label;
    let note = "";

    if (item.input === "text") {
      note = (inputs[item.id] || "").trim();
      if (!note) return; // need words for a free-write one
    } else if (item.input === "select") {
      const value = inputs[item.id] ?? item.options[0].value;
      const opt =
        item.options.find((o) => o.value === value) || item.options[0];
      label = item.lineLabel(opt); // e.g. "See a red car"
    }

    setCart((prev) => [
      ...prev,
      {
        key: newId(),
        itemId: item.id,
        label,
        emoji: item.emoji,
        recipient: activeRecipient,
        note,
      },
    ]);
    if (item.input === "text") setInput(item.id, "");

    // Flash "in the basket" on the card, bounce the dock badge, play a blip.
    playAddBlip();
    setBump((b) => b + 1);
    setAddedId(item.id);
    clearTimeout(addedTimer.current);
    addedTimer.current = setTimeout(() => setAddedId(null), 1100);
  };

  const removeItem = (key) => {
    setCart((prev) => prev.filter((line) => line.key !== key));
  };

  const placeOrder = async () => {
    if (cart.length === 0 || placing) return;
    setPlacing(true);
    playReleaseChime();
    const lines = cart.map(({ itemId, label, emoji, recipient: r, note }) => ({
      itemId,
      label,
      emoji,
      recipient: r,
      note: note || "",
    }));
    // Let the release moment breathe even if the write is instant.
    const minWait = new Promise((resolve) => setTimeout(resolve, 2700));
    try {
      // Save quietly to Firestore when configured; the ritual still completes
      // even if it isn't (e.g. before env vars are set).
      if (isFirebaseConfigured && db) {
        await addDoc(collection(db, "manifestations"), {
          lines,
          createdAt: serverTimestamp(),
        });
      }
    } catch {
      // Even if the write fails, honor the assumption: it's done.
    }
    await minWait;
    setLastOrder({
      lines,
      orderNo: newOrderNo(),
      dateLabel: new Date().toLocaleDateString("default", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    });
    setCart([]);
    try {
      localStorage.removeItem(CART_KEY);
    } catch {
      /* ignore */
    }
    setCartOpen(false);
    setPlacing(false);
    setConfirmed(true);
    window.scrollTo({ top: 0 });
  };

  const reset = () => {
    setConfirmed(false);
    setLastOrder(null);
    setRecipient("Me");
    setCustomRecipient("");
    setInputs({});
  };

  return (
    <ManifestShell>
      {placing && <ReleaseOverlay message={placingMsg} />}

      <div className="mx-auto w-full max-w-5xl px-4 pb-36 pt-10 sm:pt-14">
        {confirmed && lastOrder ? (
          <ConfirmationScreen order={lastOrder} onReset={reset} />
        ) : (
          <>
            {/* Storefront header */}
            <header className="mb-12 text-center">
              <p className="text-[11px] uppercase tracking-[0.35em] text-slate-400">
                est. before time · open 24/7 · delivers everywhere
              </p>
              <h1 className="mt-3 font-serif text-5xl font-semibold sm:text-6xl">
                <span className="ms-shimmer">Universe &amp; Co.</span>
              </h1>
              <p className="mt-2 font-serif text-base italic text-amber-200/80">
                purveyors of fine manifestations
              </p>
              <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-slate-300/90">
                Browse the aisles, tell us who it's for, and check out.
                Payment is trust. Delivery is divine timing. Everything is
                already yours.
              </p>
              <Link
                to="/manifest/log"
                className="mt-4 inline-block text-xs text-slate-500 underline-offset-4 transition-colors hover:text-slate-300 hover:underline"
              >
                🧾 Order history
              </Link>
            </header>

            {/* Ship-to picker */}
            <section className="mx-auto mb-12 max-w-3xl rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm">
              <h2 className="mb-3 flex items-center gap-2 font-serif text-lg text-amber-200">
                <span aria-hidden>📮</span> Ship to
              </h2>
              <div className="flex flex-wrap gap-2">
                {RECIPIENT_PRESETS.map((name) => {
                  const active = !customRecipient.trim() && recipient === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        setRecipient(name);
                        setCustomRecipient("");
                      }}
                      className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                        active
                          ? "border-amber-300/80 bg-amber-300/20 text-amber-100"
                          : "border-white/15 bg-white/[0.03] text-slate-300 hover:border-white/30"
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
                <input
                  type="text"
                  value={customRecipient}
                  onChange={(e) => setCustomRecipient(e.target.value)}
                  placeholder="…or someone else"
                  className="min-w-[10rem] flex-1 rounded-full border border-white/15 bg-white/[0.03] px-4 py-1.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-300/60 focus:outline-none"
                />
              </div>
              <p className="mt-3 text-xs text-slate-400">
                New wishes will be addressed to{" "}
                <span className="text-amber-200">{activeRecipient}</span>
              </p>
            </section>

            {/* Aisles */}
            {AISLES.map((aisle) => (
              <section key={aisle.id} className="mb-14">
                <div className="mb-6 flex items-center gap-4">
                  <h2 className="shrink-0 font-serif text-2xl text-white">
                    {aisle.name}
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent" />
                </div>
                <p className="-mt-4 mb-6 text-sm italic text-slate-400">
                  {aisle.tagline}
                </p>
                <div
                  className={
                    aisle.items.length === 1
                      ? "mx-auto max-w-md"
                      : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                  }
                >
                  {aisle.items.map((item) => (
                    <WishCard
                      key={item.id}
                      item={item}
                      value={inputs[item.id]}
                      onChange={(v) => setInput(item.id, v)}
                      onAdd={() => addItem(item)}
                      justAdded={addedId === item.id}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>

      {!confirmed && (
        <BasketDock
          cart={cart}
          open={cartOpen}
          onToggle={() => setCartOpen((o) => !o)}
          onRemove={removeItem}
          onRelease={placeOrder}
          placing={placing}
          bump={bump}
        />
      )}
    </ManifestShell>
  );
};

export default Manifest;
